// useMyOnboarding shares GET /api/v1/me/onboarding between the first-run gate
// and the welcome-offer banner: one call per page load, however many
// components (or shell remounts) ask for it. A failed call is not cached, so
// the next mount retries.
import {useEffect, useState} from 'react';

import {fetchMyOnboarding} from '../actions/firstRun';

let pending = null; // the in-flight or settled call
let latest = null; // last good response
const listeners = new Set();

const load = (force) => {
  if (pending && !force) {
    return pending;
  }
  const call = fetchMyOnboarding().then(
    (data) => {
      if (pending === call) {
        latest = data;
        listeners.forEach((notify) => notify(data));
      }
      return data;
    },
    (err) => {
      if (pending === call) {
        pending = null;
      }
      throw err;
    }
  );
  pending = call;
  return call;
};

// refreshMyOnboarding refetches and updates every mounted user of the hook.
export const refreshMyOnboarding = () => load(true);

// Tests only: forget everything between cases.
export const resetMyOnboarding = () => {
  pending = null;
  latest = null;
  listeners.clear();
};

// Returns the onboarding response (null until loaded or after a failure).
const useMyOnboarding = () => {
  const [onboarding, setOnboarding] = useState(latest);
  useEffect(() => {
    let mounted = true;
    const notify = (data) => mounted && setOnboarding(data);
    listeners.add(notify);
    load().then(notify, () => {}); // never block the app on a failed check
    return () => {
      mounted = false;
      listeners.delete(notify);
    };
  }, []);
  return onboarding;
};

export default useMyOnboarding;
